/**
 * Corrección de una orden de trabajo.
 *
 * Pantalla aparte de la de alta, como en los equipos, porque lo que se
 * corrige no es lo que se da de alta:
 *
 * - **El centro no se cambia aquí.** Se mueve con su propio botón, que
 *   arrastra al cliente y suelta los equipos del centro anterior. Tratarlo
 *   como un campo más lo dispararía al guardar el formulario por descuido.
 * - **El estado tampoco.** Se cambia con las transiciones, que dejan
 *   histórico de quién y por qué.
 *
 * Y lo que se puede tocar depende del estado: con el técnico ya en camino
 * se le arregla el teléfono del contacto, pero no se le mueve la fecha bajo
 * los pies. Esa regla vive en el dominio; aquí sólo se pinta.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { canEditWorkOrderField, canMoveWorkOrder, type WorkOrderStatus } from '@fibwifire/domain/browser';
import { useSession } from '../../session/session-context.js';
import { ApiError } from '../../session/api-error.js';
import { useForm } from '../../forms/use-form.js';
import { FormSection, SelectField, TextArea, TextField } from '../../forms/fields.js';
import { EntityPicker } from '../../forms/entity-picker.js';
import { isoDate, maxLength, type FieldRules } from '../../forms/form-state.js';

interface Editable {
  id: string;
  number: string;
  status: WorkOrderStatus;
  work_order_type: string;
  priority: string;
  scheduled_date: string | null;
  scheduled_time: string | null;
  estimated_minutes: number | null;
  legal_deadline: string | null;
  contract_id: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  instructions: string | null;
  notes: string | null;
  site_id: string;
  site_name: string;
  customer_name: string;
}

interface Change {
  field: string;
  old_value: string | null;
  new_value: string | null;
  reason: string | null;
  occurred_at: string;
  user_name: string | null;
}

const TYPES = [
  { value: 'PREVENTIVO', label: 'Preventivo' },
  { value: 'CORRECTIVO', label: 'Correctivo' },
  { value: 'AVISO', label: 'Aviso' },
  { value: 'INSTALACION', label: 'Instalación' },
  { value: 'TALLER', label: 'Taller' },
  { value: 'OTROS', label: 'Otros' },
];

const PRIORITIES = [
  { value: 'URGENTE', label: 'Urgente' },
  { value: 'ALTA', label: 'Alta' },
  { value: 'MEDIA', label: 'Media' },
  { value: 'BAJA', label: 'Baja' },
];

/** Cómo se llama cada campo en el histórico de correcciones. */
const CAMPOS: Record<string, string> = {
  work_order_type: 'Tipo',
  priority: 'Prioridad',
  scheduled_date: 'Fecha programada',
  scheduled_time: 'Hora',
  estimated_minutes: 'Minutos estimados',
  legal_deadline: 'Fecha límite legal',
  contract_id: 'Contrato',
  contact_name: 'Contacto',
  contact_phone: 'Teléfono',
  instructions: 'Instrucciones',
  notes: 'Notas',
  site_id: 'Centro',
  customer_id: 'Cliente',
};

const RULES: FieldRules = {
  scheduledDate: [isoDate()],
  legalDeadline: [isoDate()],
  contactName: [maxLength(255)],
  contactPhone: [maxLength(32)],
};

const NULLABLE = [
  'scheduledDate',
  'scheduledTime',
  'legalDeadline',
  'estimatedMinutes',
  'contactName',
  'contactPhone',
  'instructions',
  'notes',
];

export function WorkOrderEditScreen(): React.ReactNode {
  const { id } = useParams();
  const { api } = useSession();

  const [current, setCurrent] = useState<Editable | null>(null);
  const [changes, setChanges] = useState<Change[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const [reason, setReason] = useState('');
  const [newSiteId, setNewSiteId] = useState('');
  const [moveReason, setMoveReason] = useState('');
  const [moving, setMoving] = useState(false);
  const [moveError, setMoveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [row, history] = await Promise.all([
      api.get<Editable>(`/work-orders/${id}/editable`),
      api.get<Change[]>(`/work-orders/${id}/changes`).catch(() => [] as Change[]),
    ]);
    setCurrent(row);
    setChanges(history);
  }, [api, id]);

  useEffect(() => {
    load().catch((caught: unknown) => {
      setLoadError(
        caught instanceof ApiError ? caught.message : 'No se ha podido cargar la orden.',
      );
    });
  }, [load]);

  const form = useForm<void>({
    mode: 'edit',
    rules: RULES,
    nullable: NULLABLE,
    initial: {
      workOrderType: current?.work_order_type ?? '',
      priority: current?.priority ?? '',
      scheduledDate: current?.scheduled_date ?? '',
      scheduledTime: current?.scheduled_time ?? '',
      estimatedMinutes: current?.estimated_minutes?.toString() ?? '',
      legalDeadline: current?.legal_deadline ?? '',
      contactName: current?.contact_name ?? '',
      contactPhone: current?.contact_phone ?? '',
      instructions: current?.instructions ?? '',
      notes: current?.notes ?? '',
    },
    onSubmit: async (body, client) => {
      const payload: Record<string, unknown> = { ...body };
      const minutos = payload['estimatedMinutes'];
      if (minutos === '' || minutos === null) delete payload['estimatedMinutes'];
      else if (minutos !== undefined) payload['estimatedMinutes'] = Number(minutos);

      if (reason.trim() !== '') payload['reason'] = reason.trim();

      return client.request<void>(`/work-orders/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
    },
    onSaved: () => {
      setSaved(true);
      setReason('');
      void load();
    },
  });

  async function move(): Promise<void> {
    if (newSiteId === '') return;
    setMoveError(null);
    setMoving(true);
    try {
      await api.post(`/work-orders/${id}/site`, {
        siteId: newSiteId,
        ...(moveReason.trim() === '' ? {} : { reason: moveReason.trim() }),
      });
      setNewSiteId('');
      setMoveReason('');
      await load();
    } catch (caught) {
      setMoveError(
        caught instanceof ApiError ? caught.message : 'No se ha podido mover la orden de centro.',
      );
    } finally {
      setMoving(false);
    }
  }

  if (loadError !== null) {
    return (
      <section className="screen">
        <p className="screen__error" role="alert">
          {loadError}
        </p>
      </section>
    );
  }

  if (current === null) return <p className="screen__note">Cargando…</p>;

  const estado = current.status;
  /** Un campo bloqueado se pinta apagado con su motivo, no se esconde. */
  const bloqueo = (campo: Parameters<typeof canEditWorkOrderField>[1]): string | undefined =>
    canEditWorkOrderField(estado, campo).reason;

  const puedeMover = canMoveWorkOrder(estado);

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">{current.number}</h1>
        <span className="screen__count">
          {current.customer_name} · {current.site_name} · {estado.replace(/_/g, ' ').toLowerCase()}
        </span>
      </header>

      {saved && <p className="screen__note">Corrección guardada.</p>}

      {form.formError !== null && (
        <p className="screen__error" role="alert">
          {form.formError}
        </p>
      )}

      {form.versionConflict && (
        <p className="screen__error" role="alert">
          Alguien ha cambiado esta orden mientras la editaba. Vuelva a cargarla antes de guardar.
        </p>
      )}

      <form className="form" onSubmit={(e) => void form.submit(e)} noValidate>
        <FormSection title="Trabajo">
          <SelectField
            label="Tipo"
            value={String(form.values['workOrderType'] ?? '')}
            onChange={(v) => form.set('workOrderType', v)}
            options={TYPES}
            disabled={bloqueo('workOrderType') !== undefined}
            help={bloqueo('workOrderType')}
          />
          <SelectField
            label="Prioridad"
            value={String(form.values['priority'] ?? '')}
            onChange={(v) => form.set('priority', v)}
            options={PRIORITIES}
            disabled={bloqueo('priority') !== undefined}
            help={bloqueo('priority')}
          />
          <TextField
            label="Fecha programada"
            type="date"
            value={String(form.values['scheduledDate'] ?? '')}
            onChange={(v) => form.set('scheduledDate', v)}
            error={form.errors['scheduledDate']}
            disabled={bloqueo('scheduledDate') !== undefined}
            help={bloqueo('scheduledDate')}
          />
          <TextField
            label="Hora"
            value={String(form.values['scheduledTime'] ?? '')}
            onChange={(v) => form.set('scheduledTime', v)}
            disabled={bloqueo('scheduledTime') !== undefined}
            help="En formato 24 h, p. ej. 09:30."
          />
          <TextField
            label="Minutos estimados"
            type="number"
            value={String(form.values['estimatedMinutes'] ?? '')}
            onChange={(v) => form.set('estimatedMinutes', v)}
          />
          <TextField
            label="Fecha límite legal"
            type="date"
            value={String(form.values['legalDeadline'] ?? '')}
            onChange={(v) => form.set('legalDeadline', v)}
            error={form.errors['legalDeadline']}
            disabled={bloqueo('legalDeadline') !== undefined}
            help={bloqueo('legalDeadline') ?? 'Tope del art. 21.1: pasarla es incumplir, no llegar tarde.'}
          />
        </FormSection>

        <FormSection title="En el centro">
          <TextField
            label="Contacto"
            value={String(form.values['contactName'] ?? '')}
            onChange={(v) => form.set('contactName', v)}
            error={form.errors['contactName']}
          />
          <TextField
            label="Teléfono"
            value={String(form.values['contactPhone'] ?? '')}
            onChange={(v) => form.set('contactPhone', v)}
            error={form.errors['contactPhone']}
          />
          <TextArea
            label="Instrucciones"
            value={String(form.values['instructions'] ?? '')}
            onChange={(v) => form.set('instructions', v)}
          />
          <TextArea
            label="Notas"
            value={String(form.values['notes'] ?? '')}
            onChange={(v) => form.set('notes', v)}
          />
        </FormSection>

        <FormSection title="Por qué se corrige">
          <TextField
            label="Motivo"
            value={reason}
            onChange={setReason}
            help="Queda en el histórico junto al valor anterior. Opcional, pero es lo que hace que la corrección se pueda defender dentro de un año."
          />
        </FormSection>

        <div className="form__actions">
          <button className="button button--primary" type="submit" disabled={form.saving || !form.dirty}>
            {form.saving ? 'Guardando…' : 'Guardar la corrección'}
          </button>
          <Link className="button button--quiet" to="/ordenes">
            Volver
          </Link>
        </div>
      </form>

      {/*
        Mover de centro va fuera del formulario a propósito: arrastra al
        cliente y suelta los equipos que la orden llevaba dentro. Con su
        propio botón nadie lo dispara por pulsar «guardar».
      */}
      <FormSection title="Mover a otro centro">
        {puedeMover.allowed ? (
          <>
            <p className="screen__note">
              La orden pasará al cliente del centro de destino —es lo que hace falta cuando un local
              se traspasa— y las obligaciones del centro actual se sueltan: vuelven a estar
              pendientes de planificar, no se pierden.
            </p>
            <EntityPicker label="Centro de destino" entity="SITE" value={newSiteId} onChange={setNewSiteId} />
            <TextField label="Motivo" value={moveReason} onChange={setMoveReason} />
            {moveError !== null && (
              <p className="screen__error" role="alert">
                {moveError}
              </p>
            )}
            <button
              className="button button--quiet"
              type="button"
              disabled={moving || newSiteId === ''}
              onClick={() => void move()}
            >
              {moving ? 'Moviendo…' : 'Mover la orden'}
            </button>
          </>
        ) : (
          <p className="screen__note">{puedeMover.reason}</p>
        )}
      </FormSection>

      {changes.length > 0 && (
        <FormSection title={`Correcciones anteriores (${changes.length})`}>
          <table className="table">
            <thead>
              <tr>
                <th>Cuándo</th>
                <th>Quién</th>
                <th>Campo</th>
                <th>Antes</th>
                <th>Ahora</th>
                <th>Motivo</th>
              </tr>
            </thead>
            <tbody>
              {changes.map((c, i) => (
                <tr key={`${c.field}-${c.occurred_at}-${i}`}>
                  <td>{c.occurred_at.slice(0, 16).replace('T', ' ')}</td>
                  <td>{c.user_name ?? '—'}</td>
                  <td>{CAMPOS[c.field] ?? c.field}</td>
                  <td>{c.old_value ?? '—'}</td>
                  <td>{c.new_value ?? '—'}</td>
                  <td>{c.reason ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </FormSection>
      )}
    </section>
  );
}
